import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  CaseInputEntry,
  Expected,
  Material,
  MaterialKind,
  TargetOption,
  TargetType,
  TestCase,
  TestsApi,
} from '../../services/testsApi';
import { ExpectedEditor, KindChip, MaterialPreview } from './common';

export const errorText = (e: unknown, fallback = 'Something went wrong') => (e instanceof Error ? e.message : fallback);

export const MATERIAL_KINDS: MaterialKind[] = ['image', 'audio', 'video', 'pdf', 'text', 'json', 'file'];

function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

// ---------------------------------------------------------------------------
// Material picker
// ---------------------------------------------------------------------------

export const MaterialPickerDialog: React.FC<{
  open: boolean;
  api: TestsApi;
  multiple?: boolean;
  kinds?: MaterialKind[];
  title?: string;
  onClose: () => void;
  onPick: (materials: Material[]) => void;
}> = ({ open, api, multiple, kinds, title, onClose, onPick }) => {
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('');
  const [tag, setTag] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ items: Material[]; total: number; tags: string[] } | null>(null);
  const [selected, setSelected] = useState<Material[]>([]);
  const [error, setError] = useState<string | null>(null);
  const q = useDebounced(search);
  const pageSize = 24;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api
      .materials({ search: q || undefined, kind: kind || undefined, tag: tag || undefined, page, page_size: pageSize })
      .then(d => !cancelled && setData(d))
      .catch(e => !cancelled && setError(errorText(e)));
    return () => {
      cancelled = true;
    };
  }, [api, open, q, kind, tag, page]);

  useEffect(() => {
    if (open) setSelected([]);
  }, [open]);

  const items = (data?.items || []).filter(m => !kinds || kinds.includes(m.kind));
  const toggle = (m: Material) => {
    if (!multiple) {
      onPick([m]);
      return;
    }
    setSelected(s => (s.some(x => x.id === m.id) ? s.filter(x => x.id !== m.id) : [...s, m]));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{title || 'Choose from the library'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ my: 1 }}>
          <TextField
            size="small"
            placeholder="Search"
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(1);
            }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Kind</InputLabel>
            <Select label="Kind" value={kind} onChange={e => { setKind(e.target.value); setPage(1); }}>
              <MenuItem value="">All</MenuItem>
              {(kinds || MATERIAL_KINDS).map(k => (
                <MenuItem key={k} value={k} sx={{ textTransform: 'capitalize' }}>{k}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Tag</InputLabel>
            <Select label="Tag" value={tag} onChange={e => { setTag(e.target.value); setPage(1); }}>
              <MenuItem value="">All</MenuItem>
              {(data?.tags || []).map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
        {!data ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} /></Box>
        ) : !items.length ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No materials match.</Typography>
        ) : (
          <Grid container spacing={1.5}>
            {items.map(m => {
              const on = selected.some(x => x.id === m.id);
              return (
                <Grid item xs={6} sm={4} md={3} key={m.id}>
                  <Card variant="outlined" sx={{ borderColor: on ? 'primary.main' : undefined, borderWidth: on ? 2 : 1 }}>
                    <CardActionArea onClick={() => toggle(m)}>
                      <MaterialPreview api={api} material={m} height={110} />
                      <Box sx={{ p: 1 }}>
                        <Typography variant="body2" noWrap title={m.name}>{m.name}</Typography>
                        <KindChip kind={m.kind} />
                      </Box>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
        {data && data.total > pageSize && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination count={Math.ceil(data.total / pageSize)} page={page} onChange={(_, p) => setPage(p)} size="small" />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {multiple && (
          <Button variant="contained" disabled={!selected.length} onClick={() => onPick(selected)}>
            Use {selected.length || ''} selected
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Target picker
// ---------------------------------------------------------------------------

export const TargetPicker: React.FC<{
  api: TestsApi;
  value: TargetOption | null;
  onChange: (v: TargetOption | null) => void;
  types?: TargetType[];
  label?: string;
}> = ({ api, value, onChange, types = ['function', 'chain'], label = 'Function or chain' }) => {
  const [options, setOptions] = useState<TargetOption[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    api
      .targets()
      .then(d => !cancelled && setOptions([...d.functions, ...d.chains].filter(t => types.includes(t.target_type))))
      .catch(() => !cancelled && setOptions([]));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);
  return (
    <Autocomplete
      options={options || []}
      loading={!options}
      value={value}
      onChange={(_, v) => onChange(v)}
      groupBy={o => (o.target_type === 'function' ? 'Functions' : 'Chains')}
      getOptionLabel={o => o.name}
      isOptionEqualToValue={(a, b) => a.target_type === b.target_type && a.target_id === b.target_id}
      renderOption={(props, o) => (
        <li {...props} key={`${o.target_type}:${o.target_id}`}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1 }}>
            <span>{o.name}</span>
            <Typography variant="caption" color="text.secondary">
              {o.cases} case{o.cases === 1 ? '' : 's'}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={params => <TextField {...params} label={label} size="small" />}
      fullWidth
    />
  );
};

// ---------------------------------------------------------------------------
// Case editor
// ---------------------------------------------------------------------------

type Source = 'material' | 'text' | 'json';

interface EntryDraft {
  field: string;
  source: Source;
  material_id?: string;
  label?: string;
  text: string;
}

const toDraft = (c: TestCase | null): EntryDraft[] => {
  if (!c || !c.inputs.length) return [{ field: 'input', source: 'material', text: '' }];
  return c.inputs.map(e => {
    const preview = c.preview.find(p => p.field === e.field);
    if (e.material_id) return { field: e.field, source: 'material', material_id: e.material_id, label: preview?.text, text: '' };
    if (e.json !== undefined && e.json !== null) return { field: e.field, source: 'json', text: JSON.stringify(e.json, null, 2) };
    return { field: e.field, source: 'text', text: e.text || '' };
  });
};

const fromDraft = (entries: EntryDraft[]): { inputs: CaseInputEntry[]; error: string | null } => {
  const inputs: CaseInputEntry[] = [];
  for (const e of entries) {
    const field = e.field.trim() || 'input';
    if (e.source === 'material') {
      if (!e.material_id) return { inputs, error: `Choose a library item for "${field}".` };
      inputs.push({ field, material_id: e.material_id });
    } else if (e.source === 'json') {
      try {
        inputs.push({ field, json: JSON.parse(e.text) });
      } catch {
        return { inputs, error: `"${field}" isn't valid JSON.` };
      }
    } else {
      if (!e.text.trim()) return { inputs, error: `"${field}" is empty.` };
      inputs.push({ field, text: e.text });
    }
  }
  if (!inputs.some(i => i.field === 'input') && entries.length === 1) inputs[0].field = 'input';
  return { inputs, error: null };
};

/** Create or edit a library case. With `target`, a new case is also enabled there with `expected`. */
export const CaseEditorDialog: React.FC<{
  open: boolean;
  api: TestsApi;
  testCase?: TestCase | null;
  target?: { type: TargetType; id: string; name?: string; chain?: boolean } | null;
  onClose: () => void;
  onSaved: (c: TestCase) => void;
}> = ({ open, api, testCase, target, onClose, onSaved }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [entries, setEntries] = useState<EntryDraft[]>([]);
  const [message, setMessage] = useState('');
  const [expected, setExpected] = useState<Expected>({});
  const [advanced, setAdvanced] = useState(false);
  const [pickFor, setPickFor] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chain = !!target?.chain;

  useEffect(() => {
    if (!open) return;
    const draft = toDraft(testCase || null);
    const note = draft.find(e => e.field === 'message');
    setName(testCase?.name || '');
    setDescription(testCase?.description || '');
    setTags(testCase?.tags || []);
    setEntries(draft.filter(e => e.field !== 'message'));
    setMessage(note && note.source === 'text' ? note.text : '');
    setExpected({});
    setAdvanced(draft.filter(e => e.field !== 'message').length > 1);
    setError(null);
  }, [open, testCase]);

  const set = (i: number, patch: Partial<EntryDraft>) => setEntries(es => es.map((e, j) => (j === i ? { ...e, ...patch } : e)));

  const save = async () => {
    const { inputs, error: err } = fromDraft(entries);
    if (err) return setError(err);
    if (!name.trim()) return setError('Give the case a name.');
    if (message.trim()) inputs.push({ field: 'message', text: message.trim() });
    setSaving(true);
    setError(null);
    try {
      const body = { name: name.trim(), description: description.trim() || undefined, tags, inputs };
      const saved = testCase ? await api.updateCase(testCase.id, body) : await api.createCase(body);
      if (!testCase && target) await api.linkCases([saved.id], target.type, target.id, expected);
      onSaved(saved);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {testCase ? 'Edit test case' : target?.name ? `New test case for ${target.name}` : 'New test case'}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {testCase && testCase.links.length > 1 && (
            <Alert severity="info">This case is used by {testCase.links.length} functions or chains. Changes apply to all of them.</Alert>
          )}
          <TextField label="Name" value={name} onChange={e => setName(e.target.value)} size="small" fullWidth autoFocus />
          {entries.map((e, i) => (
            <Box key={i} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                {(advanced || chain) && (
                  <TextField
                    label="Field"
                    value={e.field}
                    onChange={ev => set(i, { field: ev.target.value })}
                    size="small"
                    sx={{ width: 160 }}
                    helperText={chain ? 'Chain input name' : undefined}
                  />
                )}
                <ToggleButtonGroup size="small" exclusive value={e.source} onChange={(_, v) => v && set(i, { source: v })}>
                  <ToggleButton value="material">Library item</ToggleButton>
                  <ToggleButton value="text">Text</ToggleButton>
                  <ToggleButton value="json">JSON</ToggleButton>
                </ToggleButtonGroup>
                <Box sx={{ flex: 1 }} />
                {entries.length > 1 && (
                  <IconButton size="small" onClick={() => setEntries(es => es.filter((_, j) => j !== i))}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
              {e.source === 'material' ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button variant="outlined" size="small" onClick={() => setPickFor(i)}>
                    {e.material_id ? 'Change' : 'Choose from the library'}
                  </Button>
                  {e.material_id && (
                    <Typography variant="body2" noWrap sx={{ maxWidth: 480 }}>
                      {e.label || e.material_id}
                    </Typography>
                  )}
                </Stack>
              ) : (
                <TextField
                  value={e.text}
                  onChange={ev => set(i, { text: ev.target.value })}
                  multiline
                  minRows={3}
                  fullWidth
                  size="small"
                  placeholder={e.source === 'json' ? '{"key": "value"}' : 'What the function receives'}
                  InputProps={{ sx: { fontFamily: e.source === 'json' ? 'monospace' : undefined } }}
                />
              )}
            </Box>
          ))}
          {!chain && (
            <TextField
              label="Note sent with the input (optional)"
              placeholder="e.g. Estimate portions for one person"
              value={message}
              onChange={e => setMessage(e.target.value)}
              size="small"
              fullWidth
              multiline
            />
          )}
          {target && !testCase && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Expected output
              </Typography>
              <ExpectedEditor value={expected} onChange={setExpected} />
            </Box>
          )}
          <Link component="button" type="button" variant="caption" onClick={() => setAdvanced(a => !a)} sx={{ alignSelf: 'flex-start' }}>
            {advanced ? 'Hide details' : 'Details: description, tags, more inputs'}
          </Link>
          <Collapse in={advanced}>
            <Stack spacing={2}>
              <TextField label="Description" value={description} onChange={e => setDescription(e.target.value)} size="small" fullWidth multiline />
              <TagInput value={tags} onChange={setTags} />
              {chain && (
                <Button
                  size="small"
                  sx={{ alignSelf: 'flex-start' }}
                  onClick={() => setEntries(es => [...es, { field: `input_${es.length + 1}`, source: 'text', text: '' }])}
                >
                  Add another input
                </Button>
              )}
            </Stack>
          </Collapse>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
      <MaterialPickerDialog
        open={pickFor !== null}
        api={api}
        onClose={() => setPickFor(null)}
        onPick={([m]) => {
          if (pickFor !== null && m) set(pickFor, { material_id: m.id, label: m.name });
          if (!name.trim() && m) setName(m.name);
          setPickFor(null);
        }}
      />
    </Dialog>
  );
};

export const TagInput: React.FC<{ value: string[]; onChange: (v: string[]) => void; options?: string[]; label?: string }> = ({
  value,
  onChange,
  options = [],
  label = 'Tags',
}) => (
  <Autocomplete
    multiple
    freeSolo
    options={options}
    value={value}
    onChange={(_, v) => onChange(Array.from(new Set(v.map(t => String(t).trim().toLowerCase()).filter(Boolean))))}
    renderTags={(v, getTagProps) => v.map((t, i) => <Chip size="small" label={t} {...getTagProps({ index: i })} key={t} />)}
    renderInput={params => <TextField {...params} label={label} size="small" placeholder="Type and press Enter" />}
  />
);

// ---------------------------------------------------------------------------
// Upload
// ---------------------------------------------------------------------------

export const UploadDialog: React.FC<{
  open: boolean;
  api: TestsApi;
  tagOptions: string[];
  maxSize: string;
  onClose: () => void;
  onDone: (result: { created: Material[]; already_in_library: Material[]; failed: { file: string; error: string }[] }) => void;
}> = ({ open, api, tagOptions, maxSize, onClose, onDone }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setFiles([]);
      setTags([]);
      setDescription('');
      setError(null);
    }
  }, [open]);

  const add = useCallback((list: FileList | null) => {
    if (!list) return;
    setFiles(f => {
      const seen = new Set(f.map(x => `${x.name}:${x.size}`));
      return [...f, ...Array.from(list).filter(x => !seen.has(`${x.name}:${x.size}`))];
    });
  }, []);

  const upload = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = { created: [] as Material[], already_in_library: [] as Material[], failed: [] as { file: string; error: string }[] };
      // Small groups keep each request well under proxy body limits.
      for (let i = 0; i < files.length; i += 5) {
        const r = await api.uploadMaterials(files.slice(i, i + 5), tags, description.trim() || undefined);
        result.created.push(...r.created);
        result.already_in_library.push(...r.already_in_library);
        result.failed.push(...r.failed);
      }
      onDone(result);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const totalMb = useMemo(() => files.reduce((s, f) => s + f.size, 0) / 1024 / 1024, [files]);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add to the library</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Box
            onDragOver={e => {
              e.preventDefault();
              setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={e => {
              e.preventDefault();
              setOver(false);
              add(e.dataTransfer.files);
            }}
            onClick={() => input.current?.click()}
            sx={{
              border: 2,
              borderStyle: 'dashed',
              borderColor: over ? 'primary.main' : 'divider',
              borderRadius: 1,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: over ? 'action.hover' : undefined,
            }}
          >
            <Typography>Drop files here or click to choose</Typography>
            <Typography variant="caption" color="text.secondary">
              Images, audio, video, PDFs, text and JSON. Up to {maxSize} each. Files already in the library are reused.
            </Typography>
            <input ref={input} type="file" multiple hidden onChange={e => { add(e.target.files); e.target.value = ''; }} />
          </Box>
          {files.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {files.length} file{files.length === 1 ? '' : 's'} · {totalMb.toFixed(1)} MB
              </Typography>
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5, mt: 0.5, maxHeight: 140, overflow: 'auto' }}>
                {files.map((f, i) => (
                  <Chip key={`${f.name}:${i}`} size="small" label={f.name} onDelete={() => setFiles(fs => fs.filter((_, j) => j !== i))} />
                ))}
              </Stack>
            </Box>
          )}
          <TagInput value={tags} onChange={setTags} options={tagOptions} label="Tags for these files" />
          <TextField label="Description (optional)" value={description} onChange={e => setDescription(e.target.value)} size="small" fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={upload} disabled={busy || !files.length}>
          {busy ? 'Uploading…' : `Upload ${files.length || ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};